'use client';
import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Hide,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
  useRadioGroup,
} from '@chakra-ui/react';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import React, { SetStateAction } from 'react';
import slugify from 'react-slugify';

import Brochure from '@/app/_components/brochure/Brochure';
import CustomImage from '@/app/_components/custom-image/CustomImage';
import CustomRadioButton from '@/app/_components/custom-radio-button/CustomRadioButton';
import LinkCustom from '@/app/_components/link/LinkCustom';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import {
  BALLOONPAYMENT,
  MAX_INTEREST,
  MIN_INTEREST,
  MONTHLYPAYMENTTERM,
} from '@/constants/payment-method/payment.constants';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { getImageLink } from '@/services/link.service';
import {
  DefaultRepaymentValue,
  getInstallementValue,
} from '@/services/new-cars/new-cars.service';
import type { ImageTransformationKeys } from '@/types/files/files.types';
import { BalloonTypes } from '@/types/filters/filters.types';
import type {
  NewCarData,
  NewVehicleColor,
  SpecificationsPricesType,
} from '@/types/new-cars/new-cars.types';

import Styles from './FinanceSelectionPage.module.scss';

const BallonPaymentButtons = ({
  value,
  onChange,
}: {
  value: string;
  onChange: React.Dispatch<SetStateAction<string>>;
}) => {
  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue: value,
    onChange: onChange,
  });

  return (
    <Box {...getRootProps()}>
      <Flex gap={'10px'}>
        {BALLOONPAYMENT.map((value, index) => {
          return (
            <CustomRadioButton
              key={index}
              radioValue={value}
              {...getRadioProps({ value: value })}
            />
          );
        })}
      </Flex>
    </Box>
  );
};
const MonthlyPaymentTermButtons = ({
  value,
  onChange,
}: {
  value: string;
  onChange: React.Dispatch<SetStateAction<string>>;
}) => {
  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue: value,
    onChange: onChange,
  });

  return (
    <Box {...getRootProps()}>
      <Flex gap={'8px'}>
        {MONTHLYPAYMENTTERM.map((value, index) => {
          return (
            <CustomRadioButton
              key={index}
              radioValue={value}
              size={'sm'}
              {...getRadioProps({ value: value })}
            />
          );
        })}
      </Flex>
    </Box>
  );
};

type Props = {
  specVehicle: SpecificationsPricesType;
  vehicleDetail: NewCarData;
  colorDetails: NewVehicleColor | undefined;
  defaultMonthlyRepaymentValues: DefaultRepaymentValue;
};

const imageKeys: ImageTransformationKeys = {
  desktopKey: 'image-815-375',
  mobileKey: 'image-320-150',
};
const FinanceSelectionPage = ({
  specVehicle,
  vehicleDetail,
  colorDetails,
  defaultMonthlyRepaymentValues,
}: Props) => {
  const { setFormItem } = useFloatingCTA();

  useEffect(() => {
    setFormItem({
      type: 'new-car',
      newCar: vehicleDetail,
      mmCode: specVehicle.mm_code,
      source: vehicleDetail?.source_bp,
    });
    return () => setFormItem(undefined);
  }, [setFormItem, specVehicle, vehicleDetail]);

  const defaultCurrentYearValue = defaultMonthlyRepaymentValues?.find(
    (monthlyPaymentTerm) => monthlyPaymentTerm?.year_offset === 0,
  );

  const [deposit, setDeposit] = useState(0);
  const [balloonPaymentType, setBalloonPaymentType] = useState('%');
  const [balloonPaymentValue, setBalloonPaymentValue] = useState(
    defaultCurrentYearValue?.baloon_payment ?? 0,
  );
  const [interestRate, setInterestRate] = useState(
    defaultCurrentYearValue?.interest_rate ?? 10,
  );
  const [monthlyPaymentTerm, setMonthlyPaymentTerm] = useState(
    defaultCurrentYearValue?.term?.toString() ?? '72',
  );
  const [estimatedPayment, setEstimatedPayment] = useState(0);

  const labelStyles = {
    mt: '3',
    fontSize: '12px',
    color: 'gray.600',
  };

  useEffect(() => {
    calculateEstimatedPayment();
  }, [deposit, balloonPaymentValue,balloonPaymentType, interestRate, monthlyPaymentTerm]);

  const calculateEstimatedPayment = async () => {
    const additionalCost = Number(
      colorDetails?.cms_new_vehicles_colours_id?.additional_cost,
    );
    const priceWithAddtionalCosts = !isNaN(additionalCost)
      ? additionalCost + Number(specVehicle.price)
      : Number(specVehicle.price);

    if (!defaultCurrentYearValue) {
      return;
    }

    const backendVal = await getInstallementValue({
      financeFilter: {
        price: Number(priceWithAddtionalCosts),
        interest: interestRate,
        paymentTerm: Number(monthlyPaymentTerm),
        balloon: balloonPaymentValue,
        deposit: deposit,
      },
      balloonOption:
        balloonPaymentType === '%'
          ? BalloonTypes.percentage
          : BalloonTypes.value,
      defaultValues: defaultCurrentYearValue,
    });

    setEstimatedPayment(backendVal?.monthlyPayment ?? 0);
  };

  const isVariantSpes = vehicleDetail?.spec_variant_specs?.some(
    (variantSpec) => specVehicle.mm_code === variantSpec?.mm_code?.mmcode,
  );

  return (
    <Box
      paddingTop={{ base: '100px', lg: '134px' }}
      paddingBottom={'0px'}
      className={Styles.financingWrapper}
    >
      <Container
        maxWidth={{ base: '100%', md: '1328px' }}
        alignContent={'center'}
        position={'relative'}
      >
        <Grid
          templateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(12, 1fr)' }}
          gap={{ base: '40px', xl: '80px' }}
        >
          <Hide breakpoint="(max-width: 991px)">
            <GridItem colSpan={{ base: 2, lg: 8 }}>
              <CustomImage
                imageSrc={
                  getImageLink(
                    colorDetails?.cms_new_vehicles_colours_id
                      ?.image_no_background_left_angle,
                  ) ?? ''
                }
                imageKeys={imageKeys}
                alt={vehicleDetail?.image_alt_tag ?? vehicleDetail?.main_title}
                className={Styles.sliderImage}
              />
            </GridItem>
          </Hide>
          <GridItem colSpan={{ base: 2, lg: 4 }}>
            <Box
              className={Styles.headingElement}
              marginBottom={{ base: '40px', lg: '48px' }}
            >
              <Text>{vehicleDetail?.main_title}</Text>
              <Heading
                as="h1"
                className={Styles.titleLg}
                marginBottom={{ base: 0, md: '8px' }}
              >
                {specVehicle?.title}
              </Heading>

              <Flex gap={'16px'} alignItems={'center'}>
                <LinkCustom
                  className="btnTextLink"
                  href={`/build-price/${vehicleDetail?.slug}?mmcode=${specVehicle?.mm_code}`}
                >
                  Change model
                </LinkCustom>
                {isVariantSpes && (
                  <LinkCustom
                    className="btnTextLink"
                    href={`/new-cars/${vehicleDetail?.slug}/specs/${specVehicle?.mm_code}`}
                  >
                    View Specs
                  </LinkCustom>
                )}
                {vehicleDetail?.brochure && (
                  <Brochure
                    model={vehicleDetail.main_title}
                    brochure={vehicleDetail.brochure}
                    variant="compact"
                  />
                )}
              </Flex>
            </Box>

            <Box className={Styles.headingElement} marginBottom={'24px'}>
              <Heading as="h2" className={Styles.titleMd}>
                Explore your finance options
              </Heading>
            </Box>
            <Stack gap={'32px'}>
              <FormControl>
                <FormLabel className={Styles.formLabel}>Deposit</FormLabel>
                <Input
                  value={`R ${deposit}`}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.-]+/g, '');
                    const price = Number(specVehicle.price);
                    const maxDeposit = Math.floor(price * 0.7);
                    const inputValue = Number(value);
                    const newDeposit = Math.min(inputValue, maxDeposit);

                    setDeposit(newDeposit);
                  }}
                  className={Styles.formInput}
                  variant="flushed"
                />
              </FormControl>
              <FormControl>
                <Flex justifyContent={'space-between'}>
                  <FormLabel className={Styles.formLabel}>
                    Balloon Payment
                  </FormLabel>
                  <BallonPaymentButtons
                    value={balloonPaymentType}
                    onChange={setBalloonPaymentType}
                  />
                </Flex>
                <InputGroup className={Styles.inputGroup}>
                  {balloonPaymentType === 'R' && (
                    <InputLeftAddon className={Styles.prefix}>R</InputLeftAddon>
                  )}
                  <Input
                    value={balloonPaymentValue}
                    onChange={(e) =>
                      setBalloonPaymentValue(
                        Number(e.target.value.replace(/[^0-9.-]+/g, '')),
                      )
                    }
                    className={Styles.formInput}
                    variant="flushed"
                  />
                  {balloonPaymentType === '%' && (
                    <InputRightAddon className={Styles.prefix}>
                      %
                    </InputRightAddon>
                  )}
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel className={Styles.formLabel}>
                  Interest Rate
                </FormLabel>
                <Slider
                  aria-label="Interest Rate"
                  defaultValue={interestRate}
                  onChange={(val) => setInterestRate(val)}
                  min={MIN_INTEREST}
                  max={MAX_INTEREST}
                >
                  <SliderMark value={MIN_INTEREST} ml={0} {...labelStyles}>
                    {MIN_INTEREST}%
                  </SliderMark>

                  <SliderMark value={MAX_INTEREST} ml={'-5'} {...labelStyles}>
                    {MAX_INTEREST}%
                  </SliderMark>
                  <SliderMark
                    value={interestRate}
                    textAlign="center"
                    color="black"
                    mt="3"
                    ml="-5"
                    w="12"
                    fontSize="16px"
                    fontWeight="600"
                    bg={'white'}
                  >
                    {interestRate}%
                  </SliderMark>
                  <SliderTrack bg={'gray.300'} h={'2px'}>
                    <SliderFilledTrack bg={'gray.600'} />
                  </SliderTrack>
                  <SliderThumb
                    border={2}
                    borderStyle={'solid'}
                    borderColor={'gray.900'}
                    boxSize={4}
                    _focusWithin={{
                      borderColor: 'gray.900',
                      boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.2)',
                    }}
                  />
                </Slider>
              </FormControl>
              <FormControl as="fieldset">
                <FormLabel as="legend" className={Styles.formLabel}>
                  Monthly Payment Term
                </FormLabel>
                <MonthlyPaymentTermButtons
                  value={monthlyPaymentTerm}
                  onChange={setMonthlyPaymentTerm}
                />
              </FormControl>
            </Stack>
          </GridItem>
        </Grid>
        <Box className={Styles.modelFooter}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            width={'100%'}
            gap={{ base: '10px', md: '24px' }}
            className={Styles.modelColorContent}
            justifyContent={'space-between'}
          >
            <HStack
              overflowX={{ base: 'auto', md: 'visible' }}
              direction={['column', 'row']}
              alignItems={'start'}
              spacing={{ base: '10px', md: '24px' }}
            >
              <Box
                minWidth={{ base: '100px', md: 'auto' }}
                width={'min-content'}
                flex={'0 0 auto'}
              >
                <Text className={Styles.labelText}>Starting at</Text>
                <Text className={cn(Styles.lableBigText, Styles.priceText)}>
                  <PriceFormat
                    prefix="R"
                    suffix="*"
                    value={specVehicle?.price}
                  />
                </Text>
                <Text className={Styles.labelSmallText}>Including VAT</Text>
              </Box>
              {colorDetails?.cms_new_vehicles_colours_id.additional_cost && (
                <>
                  <Divider
                    orientation="vertical"
                    borderColor={'gray.300'}
                    h={{ base: '73px', sm: '60px', md: '75px' }}
                  />
                  <Box
                    minWidth={{ base: '100px', md: 'auto' }}
                    width={'min-content'}
                    flex={'0 0 auto'}
                  >
                    <Text className={Styles.labelText}>Additional costs</Text>
                    <Text className={cn(Styles.lableBigText, Styles.priceText)}>
                      <PriceFormat
                        prefix="R"
                        value={
                          colorDetails?.cms_new_vehicles_colours_id
                            ?.additional_cost
                        }
                      />
                    </Text>
                    <Text className={Styles.labelSmallText}>Including VAT</Text>
                  </Box>
                </>
              )}
              <Divider
                orientation="vertical"
                borderColor={'gray.300'}
                h={{ base: '73px', sm: '60px', md: '75px' }}
              />
              <Box
                minWidth={{ base: '100px', md: 'auto' }}
                width={'min-content'}
                flex={'0 0 auto'}
              >
                <Text className={Styles.labelText}>Estimated Payment</Text>
                <Text className={cn(Styles.lableBigText, Styles.priceText)}>
                  <PriceFormat prefix="R" value={estimatedPayment.toFixed(2)} />
                </Text>
                <Text className={Styles.labelSmallText}>Per month</Text>
              </Box>
              <Divider
                orientation="vertical"
                borderColor={'gray.300'}
                h={{ base: '73px', sm: '60px', md: '75px' }}
              />
              <Box
                minWidth={{ base: '100px', md: 'auto' }}
                width={'min-content'}
                flex={'0 0 auto'}
              >
                <Text className={Styles.labelText}>Distance Warranty</Text>
                <Text className={cn(Styles.lableBigText, Styles.warrantyText)}>
                  {vehicleDetail?.images_warranty}
                </Text>
              </Box>
            </HStack>

            <Stack className={Styles.ctaBtnContainer}>
              <LinkCustom
                extraClass={Styles.ctaBtn}
                href={`/build-price/${vehicleDetail?.slug}/${specVehicle?.mm_code.toLowerCase()}/${slugify(colorDetails?.cms_new_vehicles_colours_id?.color_name)}/summary?deposit=${deposit}&balloonPayment=${balloonPaymentValue}&balloonPaymentType=${balloonPaymentType}&interestRate=${interestRate}&monthlyPaymentTerm=${monthlyPaymentTerm}&estimatedMonthlyPayment=${estimatedPayment}`}
              >
                <Text className={Styles.mobileHide}>My Summary</Text>
                <Text className={Styles.mobileShow}>Next</Text>
                <ChevronRightIcon />
              </LinkCustom>
            </Stack>
          </Flex>
        </Box>
      </Container>
    </Box>
  );
};

export default FinanceSelectionPage;
